'use client';

import { useEffect } from 'react';

interface MixpanelConfig {
  tokens: {
    development: string;
    production: string;
  };
  options: {
    throttleTime: number;
    debug: {
      enabled: boolean;
      trackInDev: boolean;
    };
  };
}

declare global {
  interface Window {
    mixpanel: any;
    MixpanelConfig?: MixpanelConfig;
    atlyTracker?: {
      trackEvent: (eventName: string, properties: Record<string, any>) => boolean;
      trackSliderShown: () => void;
      trackSeeMorePlacesClick: () => void;
      trackCTAClick: (elementName: string) => void;
      trackAppDownload: (source: string) => void;
      trackItemTapped: (tappedItem: string) => void;
      getABTestUrl: () => string;
      shouldShowReviews: () => boolean;
    };
  }
}

class AtlyTracker {
  private isInitialized = false;
  private mixpanelLoaded = false;
  private config: MixpanelConfig;
  private lastTrackedEvents: { [key: string]: number } = {};
  private throttleTime: number;
  private eventQueue: Array<{ name: string; properties: any }> = [];
  private reviewsAbTest: boolean;


  constructor() {
    this.config = window.MixpanelConfig || this.getDefaultConfig();
    this.throttleTime = this.config.options.throttleTime;
    this.reviewsAbTest = this.assignReviewsAbTest();
    this.loadMixpanelScript();
  }

  private loadMixpanelScript(): void {
    // Add Mixpanel snippet
    (function(f: any, b: any) {
      if (!b.__SV) {
        var e: any, g: any, i: any, h: any;
        window.mixpanel = b;
        b._i = [];
        b.init = function(e: any, f: any, c: any) {
          function g(a: any, d: any) {
            var b = d.split(".");
            2 == b.length && (a = a[b[0]], d = b[1]);
            a[d] = function() {
              a.push([d].concat(Array.prototype.slice.call(arguments, 0)));
            };
          }
          var a = b;
          "undefined" !== typeof c ? a = b[c] = [] : c = "mixpanel";
          a.people = a.people || [];
          a.toString = function(a: any) {
            var d = "mixpanel";
            "mixpanel" !== c && (d += "." + c);
            a || (d += " (stub)");
            return d;
          };
          a.people.toString = function() {
            return a.toString(1) + ".people (stub)";
          };
          i = "disable time_event track track_pageview track_links track_forms track_with_groups add_group set_group remove_group register register_once alias unregister identify name_tag set_config reset opt_in_tracking opt_out_tracking has_opted_in_tracking has_opted_out_tracking clear_opt_in_out_tracking start_batch_senders people.set people.set_once people.unset people.increment people.append people.union people.track_charge people.clear_charges people.delete_user people.remove".split(" ");
          for (h = 0; h < i.length; h++) g(a, i[h]);
          var j = "set set_once union unset remove delete".split(" ");
          a.get_group = function() {
            function b(c: any) {
              d[c] = function() {
                var call2_args = arguments;
                var call2 = [c].concat(Array.prototype.slice.call(call2_args, 0));
                a.push([e, call2]);
              };
            }
            for (var d: any = {}, e = ["get_group"].concat(Array.prototype.slice.call(arguments, 0)), c = 0; c < j.length; c++) b(j[c]);
            return d;
          };
          b._i.push([e, f, c]);
        };
        b.__SV = 1.2;
        e = f.createElement("script");
        e.type = "text/javascript";
        e.async = !0;
        e.src = "https://cdn.mxpnl.com/libs/mixpanel-2-latest.min.js";
        g = f.getElementsByTagName("script")[0];
        g.parentNode.insertBefore(e, g);
      }
    })(document, window.mixpanel || []);

    // Wait for Mixpanel to load and then initialize
    const checkMixpanel = setInterval(() => {
      if (window.mixpanel && typeof window.mixpanel.init === 'function') {
        clearInterval(checkMixpanel);
        this.mixpanelLoaded = true;
        this.init();
        this.processQueue();
      }
    }, 100);
  }

  private isProduction(): boolean {
    const hostname = window.location.hostname;
    const isProd = hostname === 'atly.com' || 
           hostname.includes('.atly.com') || 
           hostname.includes('production') || 
           hostname === 'www.atly.com' ||
           hostname === 'atly4seo.vercel.app' ||
           hostname.includes('vercel.app') ||
           !hostname.includes('localhost');
    
    return isProd;
  }

  private init(): void {
    if (this.isInitialized) return;
    
    const isProd = this.isProduction();
    const token = isProd 
      ? this.config.tokens.production 
      : this.config.tokens.development;
    
    try {
      window.mixpanel.init(token, {
        track_pageview: false,
        debug: !isProd && this.config.options.debug.enabled,
        persistence: 'localStorage',
        cross_subdomain_cookie: true,
        cross_site_cookie: true,
        record_sessions_percent: 2,
        record_idle_timeout_ms: 1800000,
        record_max_ms: 3600000,
        record_heatmap_data: true
      });
      this.isInitialized = true;
      
      // Only track general session start if NOT on a location page
      const isLocationPage = typeof window !== 'undefined' && window.location.pathname.includes('/location/');
      if (!isLocationPage) {
      this.trackSessionStart(); 
      }
    } catch (error) {
      console.error('[Mixpanel Tracker] Error during initialization:', error);
    }
  }

  private processQueue(): void {
    if (this.eventQueue.length > 0) {
      this.eventQueue.forEach(event => {
        this.trackEvent(event.name, event.properties, true);
      });
      this.eventQueue = [];
    }
  }

  trackEvent(eventName: string, properties: any, fromQueue = false): boolean {
    if (!this.isInitialized) {
      if (!fromQueue) {
        this.eventQueue.push({ name: eventName, properties });
      }
      return false;
    }
    
    const now = Date.now();
    const lastTracked = this.lastTrackedEvents[eventName] || 0;
    
    if (!fromQueue && this.throttleTime && (now - lastTracked < this.throttleTime)) {
      return false;
    }
    
    const isProd = this.isProduction();
    if (!isProd && !this.config.options.debug.trackInDev) {
      console.log(`[Mixpanel Tracker] DEV MODE - Skipped sending: ${eventName}`, properties);
      this.lastTrackedEvents[eventName] = now;
      return true;
    }
    
    try {
      if (this.config.options.debug.enabled) {
          console.log(`[Mixpanel Tracker] Tracking event: ${eventName}`, properties);
      }
      window.mixpanel.track(eventName, properties);
      
      this.lastTrackedEvents[eventName] = now;
      return true;
    } catch (error) {
       console.error(`[Mixpanel Tracker] Error tracking event ${eventName}:`, error);
       return false;
    }
  }

  private getDefaultConfig(): MixpanelConfig {
    return {
      tokens: {
        development: '72123733c2cb16af4962b13a8c0c1243',
        production: '72123733c2cb16af4962b13a8c0c1243'
      },
      options: {
        throttleTime: 2000,
        debug: { enabled: false, trackInDev: true }
      }
    };
  }

  private assignReviewsAbTest(): boolean {
    // Session-based truly random A/B test
    return Math.random() < 0.5;
  }

  shouldShowReviews(): boolean {
    return this.reviewsAbTest;
  }

  private assignABTest(): string {
    // Always return the topten URL (A/B test removed)
    return 'https://download.atly.com/quiz/topten';
  }

  getABTestUrl(): string {
    return this.assignABTest();
  }

  trackSessionStart(): void {
    if (!this.isInitialized) {
        console.warn('[Mixpanel Tracker] Attempted to track session start before init.'); 
        return; 
    }
    const userProperties = this.collectUserProperties();
    
    // Add reviews A/B test result to the session start event
    const sessionProperties = {
      ...userProperties,
      reviews_shown: this.reviewsAbTest
    };
    
    this.trackEvent('tt_atly4_start_session', sessionProperties);
  }



  trackSliderShown(): void {
    const properties = {
      ...this.collectUserProperties(),
      action: 'slider_shown',
      timestamp: new Date().toISOString()
    };
    
    this.trackEvent('tt_atly4_sliderShown', properties);
  }

  trackItemTapped(tappedItem: string): void {
    const properties = {
      ...this.collectUserProperties(),
      tapped_item: tappedItem,
      timestamp: new Date().toISOString()
    };
    
    this.trackEvent('tt_location_item_tapped', properties);
  }

  trackSeeMorePlacesClick(): void {
    this.trackCTAClick('see_more_places_button');
  }

  trackCTAClick(elementName: string): void {
    // Only track CTA clicks for "See More Places" button
    if (elementName === 'see_more_places_button') {
      const properties = {
        ...this.collectUserProperties(),
        action: 'cta_click',
        element: elementName,
        timestamp: new Date().toISOString()
      };
      
      this.trackEvent('tt_atly4_cta_click', properties);
    }
  }

  trackAppDownload(source = 'popup'): void {
    const properties = {
      ...this.collectUserProperties(),
      action: 'cta_click',
      element: 'get_app_button',
      source: source,
      timestamp: new Date().toISOString()
    };
    
    this.trackEvent('tt_atly4_app_download', properties);
  }

  private collectUserProperties() {
    const userAgent = navigator.userAgent;
    const isMobile = /iPhone|iPad|iPod|Android/i.test(userAgent);
    
    const screenWidth = window.innerWidth;
    const screenHeight = window.innerHeight;
    
    const referrer = document.referrer || 'direct';
    const persistentId = this.getUserId();
    
    // Extract query parameters
    const queryParams: Record<string, string> = {};
    const urlParams = new URLSearchParams(window.location.search);
    urlParams.forEach((value, key) => {
      queryParams[key] = value;
    });
    
    // Handle UTM Campaign mapping and defaults
    if (queryParams['utm_campaign']) {
      queryParams['UTM Campaign'] = queryParams['utm_campaign'];
      delete queryParams['utm_campaign']; // Remove the original lowercase version
    } else {
      queryParams['UTM Campaign'] = 'organic_seo';
    }
    
    // Set default campaign if not present
    if (!queryParams['~campaign']) {
      queryParams['~campaign'] = 'organic_seo';
    }
    
    return {
      page_title: document.title,
      url_path: window.location.pathname,
      device_type: isMobile ? 'mobile' : 'desktop',
      screen_width: screenWidth,
      screen_height: screenHeight,
      referrer: referrer,
      persistent_id: persistentId,
      timestamp: new Date().toISOString(),
      is_production: this.isProduction(),
      ...queryParams // Spread query parameters as individual properties
    };
  }

  private getUserId(): string {
    let userId = localStorage.getItem('atlyUserId');
    
    if (!userId) {
      userId = this.generateUUID();
      localStorage.setItem('atlyUserId', userId);
    }
    
    return userId;
  }

  private generateUUID(): string {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
      const r = Math.random() * 16 | 0;
      const v = c === 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
  }
}

export default function MixpanelTracker() {
  useEffect(() => {
    // Create a single instance of AtlyTracker
    const trackerInstance = new AtlyTracker();
    
    // Expose the tracker globally
    window.atlyTracker = {
      trackEvent: trackerInstance.trackEvent.bind(trackerInstance),
      trackSliderShown: trackerInstance.trackSliderShown.bind(trackerInstance),
      trackSeeMorePlacesClick: trackerInstance.trackSeeMorePlacesClick.bind(trackerInstance),
      trackCTAClick: trackerInstance.trackCTAClick.bind(trackerInstance),
      trackAppDownload: trackerInstance.trackAppDownload.bind(trackerInstance),
      trackItemTapped: trackerInstance.trackItemTapped.bind(trackerInstance),
      getABTestUrl: trackerInstance.getABTestUrl.bind(trackerInstance),
      shouldShowReviews: trackerInstance.shouldShowReviews.bind(trackerInstance)
    };

    // Cleanup function
    return () => {
      window.atlyTracker = undefined;
    };
  }, []);

  return null; // This component doesn't render anything
} 